/**
 * SELECT_N editor. For a multi-select target, keep N items from a source
 * multi-select question. An optional priority list fixes the leading items;
 * the rest are filled randomly. Produces `SELECT_N(<<source>>, N, {priority})`.
 */
import React from "react"
import { InputNumber, Select } from "antd"
import { LabelItem } from "utils/labelUtils"
import {
  QuestionInfo,
  resolveAnswerOptions,
} from "utils/logicWizard/questionTypeDetector"
import { SelectNConfig } from "utils/recodeWizard/recodeModel"
import QuestionPicker from "../LogicWizard/QuestionPicker.component"

interface SelectNEditorProps {
  value: SelectNConfig
  /** Multi-select source questions (already filtered). */
  sourceLabels: LabelItem[]
  questionInfo: Map<string, QuestionInfo>
  listMap: ReadonlyMap<string, LabelItem>
  onChange: (next: SelectNConfig) => void
}

const SelectNEditor: React.FC<SelectNEditorProps> = ({
  value,
  sourceLabels,
  questionInfo,
  listMap,
  onChange,
}) => {
  const sourceInfo = value.source ? questionInfo.get(value.source) : undefined
  const sourceOptions = sourceInfo
    ? resolveAnswerOptions(sourceInfo, listMap)
    : []

  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs text-gray-500">
        Keep N selected items from a source multi-select question. Priority items
        come first (if selected); the rest are filled in random order.
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium">Source question</span>
        <QuestionPicker
          labels={sourceLabels}
          questionInfo={questionInfo}
          value={value.source}
          onChange={(source) =>
            onChange({ ...value, source, priority: [] })
          }
          placeholder="Pick a multi-select question…"
          size="small"
        />
        <span className="text-sm font-medium ml-2">Keep</span>
        <InputNumber
          size="small"
          min={1}
          precision={0}
          value={value.n}
          onChange={(n) => onChange({ ...value, n: typeof n === "number" ? n : 1 })}
          style={{ width: 80 }}
        />
        <span className="text-sm font-medium">items</span>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium">Priority order (optional)</span>
        <Select
          mode="multiple"
          allowClear
          size="small"
          placeholder={
            value.source
              ? "Pick priority items in order…"
              : "Select a source question first"
          }
          disabled={!value.source}
          value={value.priority}
          onChange={(priority: string[]) => onChange({ ...value, priority })}
          options={sourceOptions.map((o) => ({ value: o, label: o }))}
          style={{ minWidth: 320 }}
        />
        <span className="text-xs text-gray-500">
          Leave empty for a purely random pick.
        </span>
      </div>
    </div>
  )
}

export default SelectNEditor
